import { BOOKING_STATUSES, type BookingStatus } from '@navicosa/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Card, Input, Select, Space, Switch, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import {
  BOOKING_STATUS_COLOR,
  BOOKING_STATUS_LABEL,
  type BookingListItem,
  vnd,
} from '../../api/types';

export function BookingListPage() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const page = Number(sp.get('page') ?? 1);
  const status = (sp.get('status') as BookingStatus | null) ?? undefined;
  const activeOnly = sp.get('active') !== 'false';
  const set = (k: string, v?: string) => {
    const next = new URLSearchParams(sp);
    if (v) next.set(k, v);
    else next.delete(k);
    if (k !== 'page') next.delete('page');
    setSp(next);
  };
  const list = useQuery({
    queryKey: ['bookings', page, status, activeOnly, sp.get('q')],
    queryFn: () =>
      api.page<BookingListItem>('/admin/bookings', {
        page,
        limit: 20,
        status,
        active_only: status ? undefined : activeOnly ? 'true' : undefined,
        q: sp.get('q'),
      }),
  });
  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          Đơn thuê
        </Typography.Title>
      }
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Mã DT/BG, tên khách, công ty"
          allowClear
          defaultValue={sp.get('q') ?? ''}
          onSearch={(v) => set('q', v.trim() || undefined)}
          style={{ width: 280 }}
        />
        <Select
          allowClear
          placeholder="Trạng thái"
          style={{ width: 180 }}
          value={status}
          onChange={(v) => set('status', v)}
          options={BOOKING_STATUSES.map((s) => ({ value: s, label: BOOKING_STATUS_LABEL[s] }))}
        />
        {!status && (
          <Space>
            <Switch checked={activeOnly} onChange={(v) => set('active', v ? undefined : 'false')} />
            Chỉ đơn đang thực hiện
          </Space>
        )}
      </Space>
      <Table<BookingListItem>
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data?.items}
        pagination={{
          current: page,
          pageSize: 20,
          total: list.data?.meta.total,
          onChange: (p) => set('page', String(p)),
        }}
        onRow={(r) => ({ onClick: () => nav(`/bookings/${r.id}`), style: { cursor: 'pointer' } })}
        locale={{ emptyText: 'Chưa có đơn thuê. Đơn được tạo từ báo giá khách đã chấp nhận.' }}
        columns={[
          {
            title: 'Mã đơn',
            render: (_, r) => (
              <Link to={`/bookings/${r.id}`} onClick={(e) => e.stopPropagation()}>
                {r.booking_code}
              </Link>
            ),
          },
          { title: 'Khách', render: (_, r) => r.customer.company_name ?? r.customer.full_name },
          { title: 'Khu vực', render: (_, r) => r.quotation.request.location?.name ?? '—' },
          {
            title: 'Thời gian thuê',
            render: (_, r) =>
              `${dayjs(r.rental_start_at).format('DD/MM')}${r.rental_end_at ? ` – ${dayjs(r.rental_end_at).format('DD/MM/YYYY')}` : ''}`,
          },
          {
            title: 'Xe',
            render: (_, r) => (
              <Space size={4}>
                {r.unit_count}
                {r.unassigned_count > 0 && (
                  <Tag color="orange">{r.unassigned_count} chưa gán xe</Tag>
                )}
              </Space>
            ),
          },
          { title: 'Giá trị', align: 'right', render: (_, r) => vnd(r.total_amount) },
          {
            title: 'Trạng thái',
            render: (_, r) => (
              <Tag color={BOOKING_STATUS_COLOR[r.status]}>{BOOKING_STATUS_LABEL[r.status]}</Tag>
            ),
          },
        ]}
      />
    </Card>
  );
}
